import React, { useEffect, useState, useContext } from 'react'
import { AuthSessionContext } from 'api/AuthSession'
import { AlertBanner, AlertBannerText, Button } from 'monday-ui-react-core'
import TextField from 'monday-ui-react-core/dist/TextField'
import Show from 'monday-ui-react-core/dist/icons/Show'
import Hide from 'monday-ui-react-core/dist/icons/Hide'
import { isValidEmail } from 'utils/commonUtils'
import { Link, useNavigate, redirect  } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Divider } from '@mui/material'
import './LoginForm.scss'
import { Alert } from 'react-bootstrap'

const LoginForm = () => {
    const navigate = useNavigate()
    const { authenticate, isNewPasswordRequired, changeTemporalPassword } =
        useContext(AuthSessionContext)
    const [showPassword, setShowPassword] = useState(false)
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [confirmPasswordValidation, setConfirmPasswordValidation] =
        useState('')
    const [disable, setDisable] = useState(true)
    const [disableChangePassword, setDisableChangePassword] = useState(true)
    const [errorMessage, setErrorMessage] = useState(undefined)

    const notify = () => {
        toast.success('Your password has been updated', {
            position: toast.POSITION.BOTTOM_RIGHT,
            className: 'toastify__toast--success-custom',
            bodyClassName: 'toastify-body',
            progressClassName: 'toastify-progress-bar',
        })
    }

    const isTimeout = Boolean(localStorage.getItem('isTimeout'))

    const iconValidation = () => {
        if (showPassword) {
            return <Show />
        } else {
            return <Hide />
        }
    }

    const onSubmit = async (event) => {
        event.preventDefault()
        setDisable(true)

        try {
            setErrorMessage('')
            await authenticate(email, password)
        } catch (error) {
            setErrorMessage(error.message)
        } finally {
            setDisable(false)
        }
    }

    const onSubmitNewPassword = async (event) => {
        event.preventDefault()

        if (confirmPasswordValidation !== confirmPassword) {
            setErrorMessage('Passwords must match')
            return
        }

        try {
            await changeTemporalPassword(confirmPassword)
            setErrorMessage('')
            notify()
        } catch (error) {
            setErrorMessage(error.message)
        }
    }

    useEffect(() => {
        if (isValidEmail(email) && password.length >= 8) {
            setDisable(false)
        } else {
            setDisable(true)
        }
    }, [email, password])

    useEffect(() => {
        if (
            confirmPassword.length >= 8 &&
            confirmPasswordValidation.length >= 8
        ) {
            if (confirmPassword !== confirmPasswordValidation) {
                setDisableChangePassword(true)
                setErrorMessage('Passwords must match')
            } else {
                setDisableChangePassword(false)
                setErrorMessage('')
            }
        }
    }, [confirmPassword, confirmPasswordValidation])

    const validateFields = () => {
        if (!isValidEmail(email) && email.length) {
            return true
        }
        if (!!errorMessage) {
            return true
        } else {
            return false
        }
    }

    const handleCreateAccount = () => {
        navigate('/create-account', { replace: false })
        // redirect('/create-account')
    }

    return (
        <div className="form-content">
            {!isNewPasswordRequired ? (
                <>
                    <h1>Welcome back</h1>

                    { 
                        isTimeout && 
                        <Alert variant='warning'>
                            Your session has expired. Please sign in again.
                        </Alert>
                    }

                    <p>Sign in to view your documents.</p>
                    <form className="mt-4" onSubmit={onSubmit}>
                        <TextField
                            id="email"
                            value={email}
                            onChange={(event) => setEmail(event)}
                            title="Email"
                            placeholder="Enter your email"
                            size={TextField.sizes.MEDIUM}
                            validation={{
                                status: validateFields() ? 'error' : '',
                                text:
                                    !isValidEmail(email) && email.length
                                        ? 'Please enter a valid email address.'
                                        : '',
                            }}
                            maxLength={128}
                        />
                        <div className="mt-3 mb-2">
                            <TextField
                                id="password"
                                value={password}
                                onChange={(event) => setPassword(event)}
                                wrapperClassName="input-component__icon_visibility"
                                title="Password"
                                placeholder="Enter your password"
                                size={TextField.sizes.MEDIUM}
                                type={
                                    showPassword
                                        ? TextField.types.TEXT
                                        : TextField.types.PASSWORD
                                }
                                iconName={iconValidation}
                                onIconClick={() =>
                                    setShowPassword(!showPassword)
                                }
                                validation={{
                                    status: !!errorMessage ? 'error' : '',
                                }}
                                maxLength={99}
                                minLength={8}
                            />
                        </div>
                        {errorMessage ? (
                            <p className="mt-3 text-center error-message">
                                {errorMessage}
                            </p>
                        ) : null}
                        <Link
                            to="/forgot-password"
                            className="d-inline-block mb-3 link"
                        >
                            Forgot password
                        </Link>
                        <Button
                            size={Button.sizes.LARGE}
                            disabled={disable}
                            type={Button.types.SUBMIT}
                        >
                            Sign In
                        </Button>
                        <p className="mt-3 info-text">
                            By signing in, you agree to the EnergilityDX{' '}
                            <a
                                href="https://energilitydx.com/privacy/"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Privacy Policy
                            </a>{' '}
                            and{' '}
                            <a
                                href="https://energilitydx.com/terms/"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Terms of Service
                            </a>
                            .
                        </p>
                        <Divider>OR</Divider>
                        <p className="mt-3 info-text">
                        If you received a registration code and your organization is not setup in EnergilityDX, click <b>Create Account</b>.
                        </p>
                        <Button
                            className="mt-3 create-account"
                            size={Button.sizes.LARGE}
                            type={Button.types.BUTTON}
                            onClick={handleCreateAccount}
                        >
                            Create Account
                        </Button>
                    </form>
                </>
            ) : (
                <>
                    <h1>Change your temporary password</h1>
                    <form onSubmit={onSubmitNewPassword}>
                        <TextField
                            id="password"
                            value={confirmPassword}
                            onChange={(event) => setConfirmPassword(event)}
                            wrapperClassName="input-component__icon_visibility"
                            title="New Password"
                            placeholder="Enter your new password"
                            size={TextField.sizes.MEDIUM}
                            type={TextField.types.PASSWORD}
                            validation={{
                                status: !!errorMessage ? 'error' : '',
                            }}
                            maxLength={99}
                            minLength={8}
                        />
                        <div className="mt-3 mb-2">
                            <TextField
                                id="confirm-password"
                                value={confirmPasswordValidation}
                                onChange={(event) =>
                                    setConfirmPasswordValidation(event)
                                }
                                wrapperClassName="input-component__icon_visibility"
                                title="Enter New Password Again"
                                placeholder="Enter your new password again"
                                size={TextField.sizes.MEDIUM}
                                type={TextField.types.PASSWORD}
                                validation={{
                                    status: !!errorMessage ? 'error' : '',
                                }}
                                maxLength={99}
                                minLength={8}
                            />
                            {errorMessage ? (
                                <p className="mt-3 text-center error-message">
                                    {errorMessage}
                                </p>
                            ) : null}
                        </div>
                        <section className="requirements-list pt-2">
                            <ul>
                                <li>
                                    Password must contain at least 8 characters
                                </li>
                                <li>
                                    Password must contain an upper case letter
                                </li>
                                <li>
                                    Password must contain a lower case letter
                                </li>
                                <li>Password must contain a number</li>
                                <li>
                                    Password must contain a special character or
                                    a space
                                </li>
                                <li>
                                    Password must not contain a leading or
                                    trailing space
                                </li>
                                <li>Passwords must match</li>
                            </ul>
                        </section>
                        <Button
                            size={Button.sizes.LARGE}
                            disabled={disableChangePassword}
                            type={Button.types.SUBMIT}
                        >
                            Change Password
                        </Button>
                    </form>
                </>
            )}
        </div>
    )
}

export default LoginForm
